import { FC } from 'react';
import { AssignmentItemList } from '../../models/Assignment';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { getLabelForAssignmentStudentDashboard } from '../../businessLogicService/AssignmentsAux/getLabelForAssignmentStudentDashboard';
import DotsProgressLine from './DotsProgressLine';

interface Props {
  assignment: AssignmentItemList;
  type: 'CURRENT' | 'PAST';
  // Heading level for the assignment title, so the card nests correctly under its
  // section heading wherever it's used (h4 under the home "Teacher Assignments" h3,
  // h3 under the "/assignments" page "Current" h2). Defaults to h3.
  headingLevel?: 'h3' | 'h4';
}

export const StudentDashboardAssignmentCard: FC<Props> = ({ assignment, type, headingLevel = 'h3' }) => {
  const navigate = useNavigate();
  const studentSkillAssignments = assignment.studentSkillAssignments;
  const skillsCompleted = studentSkillAssignments?.filter(s => s.submittedOn !== null).length || 0;
  const totalSkills = studentSkillAssignments?.length || 0;
  const highContrastTextStyle: React.CSSProperties = { color: '#0C1B54' };
  const Heading = headingLevel;
  return (
    <div className="flex flex-column lg:flex-row justify-content-between align-items-center p-4 border-round-lg tw-bg-neutral-0 tw-border-blue-300 tw-border-solid tw-border-4">
      <div>
        <Heading className="mb-2 font-semibold tw-text-[1.25rem] tw-leading-[1.875rem]">{assignment.name}</Heading>
        <div className="flex">
          <div className="flex tw-mr-4">
            <i className="nc_icon-nc_person icon-sm text-primary mr-2" />
            <span className="pt-1" style={highContrastTextStyle}>
              Teacher:
            </span>
            <span className="font-semibold pt-1 ml-1" style={highContrastTextStyle}>
              {`${assignment.createdBy.firstName} ${assignment.createdBy.lastName}`}
            </span>
          </div>
          <div className="tw-border-solid tw-border-gray-300"></div>
          {assignment.dueOn && (
            <div className="tw-ml-4 flex">
              <i className="nc_icon-nc_calendar icon-sm text-primary mr-2" />
              <span className="pt-1" style={highContrastTextStyle}>
                Due date:
              </span>
              <span className="font-semibold pt-1 ml-1" style={highContrastTextStyle}>
                {format(new Date(assignment.dueOn), 'P')}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="flex-grow-1 flex justify-content-center align-items-center">
        <DotsProgressLine numerator={skillsCompleted} denominator={totalSkills} />
        <p className="tw-m-4">
          <b>{skillsCompleted}</b>/{totalSkills} total skills
        </p>
      </div>
      <div>
        {type === 'CURRENT' ? (
          <Button
            label={getLabelForAssignmentStudentDashboard(assignment)}
            aria-label={`${getLabelForAssignmentStudentDashboard(assignment)} on ${assignment.name}`}
            onClick={() => navigate(`/assignment/${splitId(assignment.id)}`)}
          />
        ) : (
          <Button
            label="View"
            onClick={() => navigate(`/assignment/${splitId(assignment.id)}`)}
            outlined
            className="border-none"
          />
        )}
      </div>
    </div>
  );
};
